// Module: typesetExportCss
// -----------------------------------------------------------------
// Receives:  the tldraw container (to find the shapes' ancestors) and which
//            faces the exported content asks for
// Produces:  one stylesheet that lays out typeset shapes (KaTeX, rich text)
//            inside an SVG image exactly as the page does
// Owns:      the page rules collected for it, and each KaTeX face inlined
//            as a woff2 data URL, both cached for the session
// Does NOT:  build the SVG or draw it (→ typesetExportRenderer)
// -----------------------------------------------------------------

import { FileHelpers } from "tldraw";
import { ET_CANVAS_FONT_URLS, ET_NOTEPAD_FONT_FAMILY } from "@ever-tutor/et-ui";

/**
 * Class on the empty clones of a shape's ancestors. They exist so descendant
 * selectors (`#x .katex`, `.tl-container .tl-rich-text`) match as on the
 * page; nothing about them may paint.
 */
export const TYPESET_EXPORT_ANCESTOR_CLASS = "et-typeset-export-ancestor";

/** Which faces the exported markup asks for. */
export interface TypesetFaceNeeds {
  is_bold_used: boolean;
  is_italic_used: boolean;
  /** Every `.class` and `#id` in the markup, ancestor clones included. */
  used_tokens: ReadonlySet<string>;
}

export interface TypesetExportCss {
  css: string;
  /** False when a face the content needs could not be inlined this time. */
  is_complete: boolean;
}

// The family et-ui's math and rich-text shapes set on their containers, and
// KaTeX's letters inherit (`.tldraw-canvas .katex { font-family: inherit }`).
const HAND_FONT_FAMILY = ET_NOTEPAD_FONT_FAMILY.split(",")[0]
  .replace(/["']/g, "")
  .trim();

// What a `:root`/`html`/`body` rule may pass on: what text inherits, and
// custom properties. Anything else there (background, color-scheme) would
// paint the whole image, whose root is the <svg>.
const ROOT_TEXT_PROPERTIES = new Set([
  "line-height",
  "letter-spacing",
  "word-spacing",
  "text-rendering",
  "tab-size",
  "text-size-adjust",
  "-webkit-text-size-adjust",
  "-webkit-font-smoothing",
  "-moz-osx-font-smoothing",
]);

interface KatexFace {
  /** family|weight|style, normalised. */
  key: string;
  family: string;
  weight: string;
  style: string;
  /** Absolute woff2 URL, or the data: URL a build inlined it as. */
  woff2_url: string;
}

/** A page rule that sets a KaTeX family on whatever its selector names. */
interface KatexFaceUse {
  /** The selector's classes and ids; all present in the markup to apply. */
  tokens: string[];
  family: string;
  /** Null when the rule leaves it inherited. */
  weight: string | null;
  style: string | null;
}

interface CollectedRules {
  rules_css: string;
  root_css: string;
  katexFaces: KatexFace[];
  faceUses: KatexFaceUse[];
  /** document.styleSheets.length at collection; a new sheet re-collects. */
  sheet_count: number;
}

let collectedRules: CollectedRules | null = null;
/** One inlined @font-face per face key; null when the fetch failed. */
const inlinedFaces = new Map<string, Promise<string | null>>();

/**
 * Input:   the tldraw container; the faces the exported markup asks for
 * Output:  the stylesheet, and whether every face it needs made it in
 * Mutates: the session caches (rules on first call or after a new sheet
 *          loads, each newly needed KaTeX face once)
 *
 * KaTeX faces are chosen from the markup, through the page rules that set a
 * KaTeX family: what the browser has loaded says nothing about a formula
 * scrolled out of view, whose hidden DOM loads no fonts.
 */
export async function buildTypesetExportCss(
  container: HTMLElement,
  needs: TypesetFaceNeeds,
): Promise<TypesetExportCss> {
  if (
    !collectedRules ||
    collectedRules.sheet_count !== document.styleSheets.length
  ) {
    collectedRules = _collectRules(container);
  }
  const wantedKeys = _buildWantedFaceKeys(collectedRules.faceUses, needs);
  const faceCss = await Promise.all(
    collectedRules.katexFaces
      .filter((face) => wantedKeys.has(face.key))
      .map(_inlineFaceOnce),
  );
  return {
    css: [
      ..._buildHandFaceRules(needs),
      ...faceCss.filter((css): css is string => css !== null),
      collectedRules.root_css,
      collectedRules.rules_css,
    ].join("\n"),
    is_complete: faceCss.every((css) => css !== null),
  };
}

/** The ancestors' classes and ids as `.name` / `#name` selector tokens. */
interface AncestorTokens {
  tokens: Set<string>;
  /** Tokens a selector cannot spell unescaped (`.md:flex`), matched raw. */
  unusual: string[];
}

/**
 * Whether a page rule can affect typeset content in the export: anything
 * naming KaTeX, tldraw or ProseMirror; base rules with no class or id
 * (Tailwind preflight); rules matching the shape's ancestors or scoped under
 * one of their classes or ids (`.tldraw-canvas em`).
 */
export function isTypesetExportRule(
  selector: string,
  ancestors: Element[],
  ancestorTokens: AncestorTokens = _buildAncestorTokens(ancestors),
): boolean {
  // Document-level rules never come through whole: _buildRootDeclarations
  // takes what text inherits from them.
  if (/(^|[\s,>+~(])(:root|html|body)\b/.test(selector)) return false;
  if (/katex|tl-|ProseMirror|strut/.test(selector)) return true;
  if (!/[.#]/.test(selector)) return true;
  if (!_canTouchAncestors(selector, ancestorTokens)) return false;
  return ancestors.some(
    (el) => _safeMatches(el, selector) || _isScopedUnder(selector, el),
  );
}

function _buildAncestorTokens(ancestors: Element[]): AncestorTokens {
  const tokens = new Set<string>();
  for (const el of ancestors) {
    for (const name of Array.from(el.classList)) tokens.add(`.${name}`);
    if (el.id) tokens.add(`#${el.id}`);
  }
  return {
    tokens,
    unusual: [...tokens].filter((token) => !/^[.#][\w-]+$/.test(token)),
  };
}

/**
 * False only when the selector provably neither matches an ancestor nor is
 * scoped under one, which spares the page's thousands of unrelated rules an
 * `el.matches` per ancestor. Every class or id in a matching selector belongs
 * to the element or one of its ancestors — all of them in the chain — so a
 * list item naming none of theirs cannot match. Functional pseudo-classes
 * (`div:not(.x)`), quoted values, hex escapes and an item without a class or
 * id go to the full check.
 */
function _canTouchAncestors(
  selector: string,
  ancestorTokens: AncestorTokens,
): boolean {
  const { tokens, unusual } = ancestorTokens;
  if (/\\[0-9a-fA-F]/.test(selector)) return true;
  if (/[()"']/.test(selector.replace(/\\[\s\S]/g, ""))) return true;
  // _isScopedUnder reads the raw text: `.a\.b` holds `.b` for it.
  if (selector.match(/[.#][\w-]+/g)?.some((token) => tokens.has(token))) {
    return true;
  }
  if (unusual.some((token) => selector.includes(token))) return true;
  // Splitting on an escaped comma only makes the check more cautious: a
  // fragment without a class or id goes to the full check.
  for (const item of selector.split(",")) {
    // Tailwind escapes (`.md\:flex`), read as the class names they spell.
    const itemTokens = item
      .match(/[.#](?:[\w-]|\\[\s\S])+/g)
      ?.map((token) => token.replace(/\\([\s\S])/g, "$1"));
    if (!itemTokens) return true;
    if (itemTokens.some((token) => tokens.has(token))) return true;
  }
  return false;
}

/** Drops the session caches; for tests. */
export function resetTypesetExportCss(): void {
  collectedRules = null;
  inlinedFaces.clear();
}

function _collectRules(container: HTMLElement): CollectedRules {
  const ancestors: Element[] = [];
  const sample = container.querySelector(".tl-shape");
  for (let el = sample?.parentElement ?? null; el; el = el.parentElement) {
    ancestors.push(el);
  }
  const ancestorTokens = _buildAncestorTokens(ancestors);
  const rules: string[] = [];
  const rootDeclarations: string[] = [];
  const katexFaces: KatexFace[] = [];
  const faceKeys = new Set<string>();
  const faceUses: KatexFaceUse[] = [];
  const visit = (rule: CSSRule, href: string) => {
    if (rule instanceof CSSFontFaceRule) {
      const face = _readKatexFace(rule, href);
      // KaTeX's stylesheet can be on the page twice (app and et-ui).
      if (face && !faceKeys.has(face.key)) {
        faceKeys.add(face.key);
        katexFaces.push(face);
      }
    } else if (rule instanceof CSSStyleRule) {
      faceUses.push(..._readKatexFaceUses(rule));
      if (_isDocumentRule(rule.selectorText)) {
        rootDeclarations.push(..._buildRootDeclarations(rule.style));
      } else if (
        isTypesetExportRule(rule.selectorText, ancestors, ancestorTokens)
      ) {
        rules.push(rule.cssText);
      }
    }
  };
  for (const sheet of Array.from(document.styleSheets)) {
    _walkSheet(sheet, visit);
  }
  rules.push(..._buildExportOnlyRules());
  return {
    rules_css: rules.join("\n"),
    // `:root` inside the image is the <svg>, whose text settings the
    // foreignObject content inherits as the page's content does from <html>.
    root_css:
      rootDeclarations.length > 0
        ? `:root { ${rootDeclarations.join(" ")} }`
        : "",
    katexFaces,
    faceUses,
    sheet_count: document.styleSheets.length,
  };
}

/**
 * Every rule that applies on the page, with @media and @supports resolved
 * against the page: inside the image a media query would be asked of the
 * image's own size. The app ships no @layer blocks.
 */
function _walkSheet(
  sheet: CSSStyleSheet,
  visit: (rule: CSSRule, href: string) => void,
): void {
  let list: CSSRuleList;
  try {
    list = sheet.cssRules;
  } catch {
    return; // cross-origin (Google Fonts): holds nothing typeset
  }
  _walkRuleList(list, sheet.href ?? document.baseURI, visit);
}

function _walkRuleList(
  list: CSSRuleList,
  href: string,
  visit: (rule: CSSRule, href: string) => void,
): void {
  for (const rule of Array.from(list)) {
    if (rule instanceof CSSImportRule) {
      if (rule.styleSheet && _doesPageMatchMedia(rule.media.mediaText)) {
        _walkSheet(rule.styleSheet, visit);
      }
    } else if (rule instanceof CSSMediaRule) {
      if (_doesPageMatchMedia(rule.media.mediaText)) {
        _walkRuleList(rule.cssRules, href, visit);
      }
    } else if (
      typeof CSSSupportsRule !== "undefined" &&
      rule instanceof CSSSupportsRule
    ) {
      if (_doesPageSupport(rule.conditionText)) {
        _walkRuleList(rule.cssRules, href, visit);
      }
    } else {
      visit(rule, href);
    }
  }
}

function _doesPageMatchMedia(mediaText: string): boolean {
  if (!mediaText.trim() || mediaText.trim() === "all") return true;
  return window.matchMedia?.(mediaText).matches ?? false;
}

function _doesPageSupport(conditionText: string): boolean {
  try {
    return CSS.supports(conditionText);
  } catch {
    return false;
  }
}

function _buildExportOnlyRules(): string[] {
  return [
    // Page rules on the ancestors (`#root { height: 100%; background: … }`,
    // tldraw's `.tl-canvas { content-visibility: auto }`, which clips to a
    // zero-height clone) must neither paint nor hide the shapes.
    `.${TYPESET_EXPORT_ANCESTOR_CLASS} { background: transparent !important; border: 0 !important; box-shadow: none !important; outline: 0 !important; height: auto !important; min-height: 0 !important; content-visibility: visible !important; contain: none !important; }`,
  ];
}

/**
 * Whether every selector in the list is `:root`, `html`, `body` or `:host`
 * itself — Tailwind's preflight sets its text defaults on `html, :host`.
 */
function _isDocumentRule(selector: string): boolean {
  return selector
    .split(",")
    .every((part) => /^(:root|html|body|:host)$/.test(part.trim()));
}

function _buildRootDeclarations(style: CSSStyleDeclaration): string[] {
  return Array.from(style)
    .filter(
      (name) =>
        name.startsWith("--") ||
        name.startsWith("font-") ||
        ROOT_TEXT_PROPERTIES.has(name),
    )
    .map((name) => {
      const priority = style.getPropertyPriority(name) ? " !important" : "";
      return `${name}: ${style.getPropertyValue(name)}${priority};`;
    });
}

/** The hand-drawn face, regular always, other variants only when used. */
function _buildHandFaceRules(needs: TypesetFaceNeeds): string[] {
  const variants: [string, string, string, boolean][] = [
    [ET_CANVAS_FONT_URLS.tldraw_draw, "400", "normal", true],
    [ET_CANVAS_FONT_URLS.tldraw_draw_bold, "700", "normal", needs.is_bold_used],
    [
      ET_CANVAS_FONT_URLS.tldraw_draw_italic,
      "400",
      "italic",
      needs.is_italic_used,
    ],
    [
      ET_CANVAS_FONT_URLS.tldraw_draw_italic_bold,
      "700",
      "italic",
      needs.is_bold_used && needs.is_italic_used,
    ],
  ];
  return variants
    .filter(([, , , is_used]) => is_used)
    .map(
      ([url, weight, style]) =>
        `@font-face { font-family: "${HAND_FONT_FAMILY}"; font-weight: ${weight}; font-style: ${style}; font-display: block; src: url("${url}") format("woff2"); }`,
    );
}

/**
 * The faces of every rule whose classes and ids all occur in the markup. A
 * weight or style the rule leaves inherited is taken as regular, plus bold or
 * italic when the content uses them anywhere.
 */
function _buildWantedFaceKeys(
  uses: KatexFaceUse[],
  needs: TypesetFaceNeeds,
): Set<string> {
  const inheritedWeights = needs.is_bold_used ? ["400", "700"] : ["400"];
  const inheritedStyles = needs.is_italic_used
    ? ["normal", "italic"]
    : ["normal"];
  const keys = new Set<string>();
  for (const use of uses) {
    if (!use.tokens.every((token) => needs.used_tokens.has(token))) continue;
    for (const weight of use.weight ? [use.weight] : inheritedWeights) {
      for (const style of use.style ? [use.style] : inheritedStyles) {
        keys.add(`${use.family}|${weight}|${style}`);
      }
    }
  }
  return keys;
}

function _readKatexFaceUses(rule: CSSStyleRule): KatexFaceUse[] {
  const family = rule.style
    .getPropertyValue("font-family")
    .split(",")
    .map((name) => name.replace(/["']/g, "").trim())
    .find((name) => name.startsWith("KaTeX_"));
  if (!family) return [];
  const weight = _normaliseWeight(rule.style.getPropertyValue("font-weight"));
  const style = _normaliseStyle(rule.style.getPropertyValue("font-style"));
  return rule.selectorText.split(",").map((part) => ({
    tokens: part.replace(/:not\([^)]*\)/g, "").match(/[.#][\w-]+/g) ?? [],
    family,
    weight,
    style,
  }));
}

/**
 * Whether the selector names one of the element's classes or its id as a
 * whole token — `.tldraw-canvas em` for `.tldraw-canvas`, not `.tldraw-canvas-x`.
 */
function _isScopedUnder(selector: string, el: Element): boolean {
  const tokens = [
    ...Array.from(el.classList, (name) => `.${name}`),
    ...(el.id ? [`#${el.id}`] : []),
  ];
  return tokens.some((token) => {
    let index = selector.indexOf(token);
    while (index >= 0) {
      const next = selector.charAt(index + token.length);
      if (!/[\w-]/.test(next)) return true;
      index = selector.indexOf(token, index + 1);
    }
    return false;
  });
}

function _readKatexFace(rule: CSSFontFaceRule, href: string): KatexFace | null {
  const family = rule.style.getPropertyValue("font-family");
  if (!family.includes("KaTeX")) return null;
  // Some engines keep `src` only in cssText.
  const src = rule.style.getPropertyValue("src") || rule.cssText;
  const woff2_url = _findWoff2Url(src, href);
  if (!woff2_url) return null;
  const cleanFamily = family.replace(/["']/g, "").trim();
  const weight = _normaliseWeight(rule.style.getPropertyValue("font-weight"));
  const style = _normaliseStyle(rule.style.getPropertyValue("font-style"));
  return {
    key: `${cleanFamily}|${weight ?? "400"}|${style ?? "normal"}`,
    family: cleanFamily,
    weight: weight ?? "400",
    style: style ?? "normal",
    woff2_url,
  };
}

/**
 * The woff2 source of an @font-face `src`: every browser that renders this
 * export reads woff2, and the woff and ttf copies would triple the weight. A
 * production build inlines the smallest faces as data: URLs.
 */
function _findWoff2Url(src: string, href: string): string | null {
  const sources = src.matchAll(
    /url\(\s*(["']?)(.*?)\1\s*\)(?:\s*format\(\s*["']?([\w-]+)["']?\s*\))?/g,
  );
  for (const [, , url, format] of sources) {
    const is_woff2 =
      format === "woff2" ||
      /^data:[\w/+.-]*woff2/.test(url) ||
      /\.woff2([?#]|$)/.test(url);
    if (!is_woff2) continue;
    return url.startsWith("data:") ? url : new URL(url, href).href;
  }
  return null;
}

function _normaliseWeight(weight: string): string | null {
  const value = weight.trim();
  if (value === "normal") return "400";
  if (value === "bold") return "700";
  return /^\d+$/.test(value) ? value : null;
}

function _normaliseStyle(style: string): string | null {
  const value = style.trim();
  return value === "normal" || value === "italic" ? value : null;
}

function _inlineFaceOnce(face: KatexFace): Promise<string | null> {
  let inlined = inlinedFaces.get(face.key);
  if (!inlined) {
    inlined = _inlineWoff2Face(face);
    inlinedFaces.set(face.key, inlined);
  }
  return inlined;
}

/**
 * The face as an @font-face whose only source is its woff2, inlined. A fetch
 * that fails yields null and is retried on the next call.
 */
async function _inlineWoff2Face(face: KatexFace): Promise<string | null> {
  try {
    let dataUrl = face.woff2_url;
    if (!dataUrl.startsWith("data:")) {
      const response = await fetch(dataUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      dataUrl = await FileHelpers.blobToDataUrl(await response.blob());
    }
    return `@font-face { font-family: ${face.family}; font-weight: ${face.weight}; font-style: ${face.style}; font-display: block; src: url("${dataUrl}") format("woff2"); }`;
  } catch (error) {
    console.warn(
      "[typesetExportCss] KaTeX face not inlined",
      face.woff2_url,
      error,
    );
    inlinedFaces.delete(face.key);
    return null;
  }
}

function _safeMatches(el: Element, selector: string): boolean {
  try {
    return el.matches(selector);
  } catch {
    return false;
  }
}
